import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm, Image, Tag, Typography, Switch, Upload, Checkbox, Radio, InputNumber } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, StarFilled, StarOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { productAPI, categoryAPI, dropshipperAPI, exchangeRateAPI, collectionAPI } from '../api';

const { Option } = Select;
const { Text } = Typography;

// 获取API基础URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const ProductManagement = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [freightChannels, setFreightChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // 分页相关状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
  });
  const [searchKeyword, setSearchKeyword] = useState('');
  
  // CSV导入相关状态
  const [csvModalVisible, setCsvModalVisible] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvForm] = Form.useForm();
  const [taskStatusVisible, setTaskStatusVisible] = useState(false);
  const [taskStatus, setTaskStatus] = useState(null);
  const [taskPolling, setTaskPolling] = useState(null);
  const [stableTaskStatus, setStableTaskStatus] = useState(null); // 稳定的任务状态，减少闪烁
  const [pollingInstanceId, setPollingInstanceId] = useState(null); // 轮询实例ID，用于调试
  const [csvImportLoading, setCsvImportLoading] = useState(false); // CSV导入按钮loading状态
  const [showCsvButton, setShowCsvButton] = useState('none'); // CSV按钮显示状态: 'none', 'import', 'processing'
  
  // 新增功能选项状态
  const [convertPriceByExchangeRate, setConvertPriceByExchangeRate] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(7.2); // 默认汇率
  const [selectedCurrency, setSelectedCurrency] = useState('USD'); // 币种选择
  const [percentageMarkup, setPercentageMarkup] = useState(false);
  const [markupPercent, setMarkupPercent] = useState(50);
  const [priceRounding, setPriceRounding] = useState(false);
  const [roundingType, setRoundingType] = useState('ceil'); // 'ceil' 或 '99'
  const [minWeight, setMinWeight] = useState(false);
  const [minWeightValue, setMinWeightValue] = useState(200);
  const [addWeight, setAddWeight] = useState(false);
  const [weightType, setWeightType] = useState('fixed'); // 'fixed' 或 'percentage'
  const [weightValue, setWeightValue] = useState(200);
  
  // AI处理相关状态
  const [aiCleanDescription, setAiCleanDescription] = useState(false);
  const [aiTranslateTitle, setAiTranslateTitle] = useState(false);
  const [aiTranslateVariant, setAiTranslateVariant] = useState(false);
  
  // 图片下载相关状态
  const [noDownloadImages, setNoDownloadImages] = useState(false);
  
  // 来源信息相关状态
  const [sourceInfoVisible, setSourceInfoVisible] = useState(false);
  const [currentSourceInfo, setCurrentSourceInfo] = useState(null);

  // 商品备注相关状态
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [notesTargetProduct, setNotesTargetProduct] = useState(null);
  const [notesValue, setNotesValue] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);

  const [bulkNotesModalVisible, setBulkNotesModalVisible] = useState(false);
  const [bulkNotesContent, setBulkNotesContent] = useState('');
  const [bulkNotesPosition, setBulkNotesPosition] = useState('after');
  const [bulkNotesLoading, setBulkNotesLoading] = useState(false);
  const [bulkNotesDeleteVisible, setBulkNotesDeleteVisible] = useState(false);
  const [bulkNotesDeleteLoading, setBulkNotesDeleteLoading] = useState(false);

  // 分类过滤相关状态
  const [categoryFilterVisible, setCategoryFilterVisible] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({ categories: [], search: '' });
  const [sorterState, setSorterState] = useState({ field: null, order: null });

  // 批量操作相关状态
  const [selectedProductIds, setSelectedProductIds] = useState([]); // 选中的商品ID列表
  const [selectAllPages, setSelectAllPages] = useState(false); // 是否跨页全选
  const [batchDeleteVisible, setBatchDeleteVisible] = useState(false);
  const [batchStatusVisible, setBatchStatusVisible] = useState(false);
  const [batchCategoryVisible, setBatchCategoryVisible] = useState(false);
  const [batchFreightVisible, setBatchFreightVisible] = useState(false);
  const [batchStatusValue, setBatchStatusValue] = useState(true);
  const [batchCategoryValue, setBatchCategoryValue] = useState(null);
  const [batchFreightValue, setBatchFreightValue] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);

  // 稳定化任务状态，减少UI闪烁
  useEffect(() => {
    if (taskStatus && taskStatus.has_task) {
      // 有任务时，实时更新稳定状态（保持进度数据最新）
      setStableTaskStatus(taskStatus);
      // 只有在任务正在处理时才设置为processing，完成或失败时不覆盖
      if (taskStatus.status === 'processing' || taskStatus.status === 'pending') {
        setShowCsvButton('processing');
      }
    } else if (taskStatus && !taskStatus.has_task) {
      // 没有任务时，设置明确的无任务状态，而不是清空
      setStableTaskStatus({ has_task: false });
      setShowCsvButton('import');
    }
  }, [taskStatus]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchCollections();
    fetchFreightChannels();
    fetchExchangeRate();
  }, []);
  
  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      if (taskPolling) {
        
        clearInterval(taskPolling);
      }
    };
  }, [taskPolling, pollingInstanceId]);

  const fetchProducts = useCallback(async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    categories = appliedFilters.categories,
    keyword = searchKeyword,
    sortField = sorterState.field,
    sortOrder = sorterState.order
  ) => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (categories && categories.length > 0) {
        params.categories = categories;
      }
      if (keyword) {
        params.search = keyword;
      }
      if (sortField && sortOrder) {
        params.sort_by = sortField;
        params.sort_order = sortOrder === 'ascend' ? 'asc' : 'desc';
      }
      
      const response = await productAPI.getAll(params);
      
      // 处理分页响应
      setProducts(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        current: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      }));
      
      // 更新应用的过滤器状态
      const filterData = response.data.filter || {};
      setAppliedFilters({
        categories: Array.isArray(filterData.categories) ? filterData.categories : [],
        search: typeof filterData.search === 'string' ? filterData.search : (keyword || ''),
      });
    } catch (error) {
      message.error('获取商品列表失败');
      
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, appliedFilters.categories, searchKeyword, sorterState.field, sorterState.order]);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      message.error('获取分类列表失败');
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await collectionAPI.getAll();
      setCollections(response.data || []);
    } catch (error) {
      message.error('获取商品集列表失败');
    }
  };

  const fetchFreightChannels = async () => {
    try {
      const response = await dropshipperAPI.getFreightChannels();
      setFreightChannels(response.data);
    } catch (error) {
      message.error('获取物流渠道失败');
    }
  };

  const fetchExchangeRate = async () => {
    try {
      const response = await exchangeRateAPI.getByCurrency('CNY');
      setExchangeRate(response.data.rate_to_usd || 7.2);
    } catch (error) {
      
      setExchangeRate(7.2);
    }
  };

const handleAdd = () => {
  navigate('/admin/products/create');
};

const handleEdit = (record) => {
  window.open(`/admin/products/edit/${record.id}`, '_blank');
};  const handleOpenNotesModal = async (record) => {
    setNotesTargetProduct(record);
    setNotesModalVisible(true);
    setNotesValue('');
    setNotesLoading(true);
    try {
      const response = await productAPI.getNotes(record.id);
      setNotesValue(response.data?.notes || '');
    } catch (error) {
      message.error('获取备注失败');
    } finally {
      setNotesLoading(false);
    }
  };

  const handleCloseNotesModal = () => {
    if (notesSaving) return;
    setNotesModalVisible(false);
    setNotesTargetProduct(null);
    setNotesValue('');
    setNotesLoading(false);
  };

  const handleSubmitNotes = async () => {
    if (!notesTargetProduct) return;
    setNotesSaving(true);
    try {
      await productAPI.updateNotes(notesTargetProduct.id, { notes: notesValue });
      message.success('备注已更新');
      setNotesModalVisible(false);
      setNotesTargetProduct(null);
      setNotesValue('');
    } catch (error) {
      message.error('更新备注失败');
    } finally {
      setNotesSaving(false);
    }
  };


  const handleOpenBulkNotesModal = () => {
    setBulkNotesContent('');
    setBulkNotesPosition('after');
    setBulkNotesModalVisible(true);
  };

  const handleCloseBulkNotesModal = () => {
    if (bulkNotesLoading) return;
    setBulkNotesModalVisible(false);
    setBulkNotesContent('');
    setBulkNotesPosition('after');
  };

  const handleBatchAppendNotes = async () => {
    if (!bulkNotesContent.trim()) {
      message.warning('请输入要追加的备注内容');
      return;
    }

    setBulkNotesLoading(true);
    const payload = selectAllPages ? {
      use_filter: true,
      filter_categories: appliedFilters.categories.length > 0 ? appliedFilters.categories : null,
      content: bulkNotesContent,
      position: bulkNotesPosition
    } : {
      use_filter: false,
      product_ids: selectedProductIds,
      content: bulkNotesContent,
      position: bulkNotesPosition
    };

    try {
      await productAPI.appendNotesBatch(payload);
      message.success('备注已追加');
      setBulkNotesModalVisible(false);
      setBulkNotesContent('');
      setBulkNotesPosition('after');
      setSelectedProductIds([]);
      setSelectAllPages(false);
      fetchProducts();
    } catch (error) {
      message.error('批量追加备注失败');
    } finally {
      setBulkNotesLoading(false);
    }
  };

  const handleBatchDeleteNotes = async () => {
    setBulkNotesDeleteLoading(true);
    const payload = selectAllPages ? {
      use_filter: true,
      filter_categories: appliedFilters.categories.length > 0 ? appliedFilters.categories : null,
    } : {
      use_filter: false,
      product_ids: selectedProductIds,
    };

    try {
      await productAPI.clearNotesBatch(payload);
      message.success('备注已删除');
      setBulkNotesDeleteVisible(false);
      setSelectedProductIds([]);
      setSelectAllPages(false);
      fetchProducts();
    } catch (error) {
      message.error('批量删除备注失败');
    } finally {
      setBulkNotesDeleteLoading(false);
    }
  };


  // 计算规格数量的辅助函数
  const countVariants = (variantsData) => {
    if (!variantsData) return 0;
    
    // 只处理数组格式
    if (Array.isArray(variantsData)) {
      return variantsData.length;
    }
    
    return 0;
  };

  // 计算价格区间展示
  const getPriceRangeDisplay = (variantsData) => {
    if (!Array.isArray(variantsData) || variantsData.length === 0) return '-';
    const prices = variantsData
      .map(v => Number(v.price))
      .filter(price => !Number.isNaN(price));
    if (prices.length === 0) return '-';
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const currency = variantsData.find(v => v && v.currency)?.currency || '';
    if (minPrice === maxPrice) {
      return `${minPrice}${currency}`;
    }
    return `${minPrice}-${maxPrice}${currency}`;
  };

  const handleDelete = async (id) => {
    try {
      await productAPI.delete(id);
      message.success('删除成功');
      fetchProducts();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 处理分页、排序、筛选变化
  const handleTableChange = (paginationConfig, filters, sorter) => {
    const { current, pageSize } = paginationConfig;
    // 如果不是跨页全选模式，切换页面时清除选中状态
    if (!selectAllPages) {
      setSelectedProductIds([]);
    }
    const sorterInfo = Array.isArray(sorter) ? (sorter[0] || {}) : (sorter || {});
    let sortField = sorterState.field;
    let sortOrder = sorterState.order;
    if (sorterInfo && (sorterInfo.field || sorterInfo.columnKey || sorterInfo.order)) {
      sortField = sorterInfo.field || sorterInfo.columnKey || null;
      sortOrder = sorterInfo.order || null;
    }
    if (!sortOrder) {
      sortField = null;
    }
    setSorterState({ field: sortField, order: sortOrder });
    fetchProducts(current, pageSize, appliedFilters.categories, searchKeyword, sortField, sortOrder);
  };

  const handleSearch = (value) => {
    const trimmed = value.trim();
    setSearchKeyword(trimmed);
    setPagination(prev => ({
      ...prev,
      current: 1,
    }));
    fetchProducts(1, pagination.pageSize, appliedFilters.categories, trimmed);
  };

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchKeyword(value);
    if (value === '') {
      setPagination(prev => ({
        ...prev,
        current: 1,
      }));
      fetchProducts(1, pagination.pageSize, appliedFilters.categories, '');
    }
  };

  // 设置封面图片
  const handleSetCover = async (productId, imageId) => {
    try {
      await productAPI.setCoverImage(productId, imageId);
      message.success('封面图片设置成功');
      fetchProducts(); // 刷新产品列表
    } catch (error) {
      message.error('设置封面图片失败');
    }
  };

  // 打开图片画廊
  const handleOpenGallery = (product) => {
    setCurrentProduct(product);
    setCurrentImageIndex(0);
    setGalleryVisible(true);
  };

  // 切换图片
  const handleImageChange = (direction) => {
    if (!currentProduct?.images) return;
    
    const maxIndex = currentProduct.images.length - 1;
    if (direction === 'prev') {
      setCurrentImageIndex(prev => prev > 0 ? prev - 1 : maxIndex);
    } else {
      setCurrentImageIndex(prev => prev < maxIndex ? prev + 1 : 0);
    }
  };


  // CSV导入相关函数
  const handleOpenCsvImport = () => {
    setCsvModalVisible(true);
    csvForm.resetFields();
    setCsvFile(null);
    setCsvImportLoading(false); // 重置loading状态
    
    // 重置新功能选项状态
    setConvertPriceByExchangeRate(false);
    setSelectedCurrency('USD');
    setPercentageMarkup(false);
    setMarkupPercent(50);
    setPriceRounding(false);
    setRoundingType('ceil');
    setMinWeight(false);
    setMinWeightValue(200);
    setAddWeight(false);
    setWeightType('fixed');
    setWeightValue(200);
    setAiCleanDescription(false);
    setAiTranslateTitle(false);
    setAiTranslateVariant(false);
    setNoDownloadImages(false);
    fetchExchangeRate(); // 重新获取最新汇率
  };

  const handleCsvFileChange = (info) => {
     // 调试日志
    if (info.file.status === 'removed') {
      setCsvFile(null);
    } else {
      // 直接使用文件，不依赖状态
      setCsvFile(info.file.originFileObj || info.file);
    }
  };

  const handleCsvUpload = async (values) => {
    if (!csvFile) {
      message.error('请选择CSV文件');
      return;
    }

    // 设置loading状态，防止重复点击
    setCsvImportLoading(true);

    // 构建additional_features参数
    const additionalFeatures = {
      convert_price_by_exchange_rate: convertPriceByExchangeRate,
      exchange_rate: exchangeRate,
      selected_currency: selectedCurrency,
      percentage_markup: percentageMarkup,
      markup_percent: markupPercent,
      price_rounding: priceRounding,
      rounding_type: roundingType,
      min_weight: minWeight,
      min_weight_value: minWeightValue,
      add_weight: addWeight,
      weight_type: weightType,
      weight_value: weightValue,
      ai_clean_description: aiCleanDescription,
      ai_translate_title: aiTranslateTitle,
      ai_translate_variant: aiTranslateVariant,
      no_download_images: noDownloadImages
    };

    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('category_id', values.category_id);
    formData.append('supported_freight_channels', JSON.stringify(values.supported_freight_channels || []));
    formData.append('collection_ids', JSON.stringify(values.collection_ids || []));
    formData.append('is_active', values.is_active !== false);
    formData.append('additional_features', JSON.stringify(additionalFeatures));

    try {
      const response = await fetch(`${API_BASE_URL}/csv-import/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        message.success('CSV上传成功，开始处理');
        setCsvModalVisible(false);
        
        // 设置初始任务状态
        setTaskStatus({
          has_task: true,
          status: 'processing',
          task_id: result.task_id,
          total_rows: 0,
          processed_rows: 0,
          products_found: 0,
          variants_found: 0,
          images_downloaded: 0,
          queue_size: 1
        });
        setShowCsvButton('processing'); // 设置按钮状态为处理中
        
        // 如果还没有轮询在运行，才开始轮询
        if (!taskPolling) {
          
          startTaskPolling();
        } else {
          
        }
      } else {
        const error = await response.json();
        message.error(error.detail || 'CSV上传失败');
        setCsvImportLoading(false); // 失败时清除loading状态
      }
    } catch (error) {
      
      message.error('CSV上传失败');
      setCsvImportLoading(false); // 失败时清除loading状态
    }
  };

  const startTaskPolling = useCallback(() => {
    // 生成唯一的轮询实例ID用于调试
    const instanceId = Date.now() + '-' + Math.random().toString(36).substring(2, 9);
    
    
    
    // 清除现有轮询
    if (taskPolling) {
      
      clearInterval(taskPolling);
      setTaskPolling(null);
      setPollingInstanceId(null);
    }
    
    
    
    const polling = setInterval(async () => {
      try {
        
        const response = await fetch(`${API_BASE_URL}/csv-import/status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        if (response.ok) {
          const status = await response.json();
          
          
          // 只有当获取到有效状态时才更新，保持上一次的状态直到新状态返回
          setTaskStatus(prevStatus => {
            // 如果新状态有效且有任务，使用新状态
            if (status && typeof status === 'object' && status.has_task) {
              return status;
            }
            // 如果新状态明确表示没有任务，也需要更新
            if (status && typeof status === 'object' && !status.has_task) {
              return status;
            }
            // 其他情况保持原状态
            return prevStatus;
          });
          
          if (status.has_task && (status.status === 'completed' || status.status === 'error')) {
            // 任务完成，停止轮询
            
            clearInterval(polling);
            setTaskPolling(null);
            setPollingInstanceId(null);
            
            if (status.status === 'completed') {
              message.success('CSV导入完成');
              fetchProducts(); // 刷新商品列表
              setShowCsvButton('import'); // 更新按钮状态为显示导入按钮
            } else {
              message.error(`CSV导入失败: ${status.error_message}`);
              setShowCsvButton('import'); // 失败时也显示导入按钮
            }
          } else if (!status.has_task) {
            // 没有任务，停止轮询
            
            clearInterval(polling);
            setTaskPolling(null);
            setPollingInstanceId(null);
            setShowCsvButton('import'); // 没有任务时显示导入按钮
          }
          // 如果status是pending或processing，继续轮询
        } else {
          
        }
      } catch (error) {
        
        // 网络错误时不清空状态，保持上一次的状态
      }
    }, 3000); // 3秒轮询间隔
    
    setTaskPolling(polling);
    setPollingInstanceId(instanceId);
    
  }, [fetchProducts]); // fetchProducts现在是稳定的

  const handleShowTaskStatus = () => {
    setTaskStatusVisible(true);
  };

  // 显示来源信息对话框
  const handleShowSourceInfo = (sourceInfo) => {
    setCurrentSourceInfo(sourceInfo);
    setSourceInfoVisible(true);
  };

  // 分类过滤相关辅助函数
  const handleClearSelection = () => {
    setSelectedCategories([]);
  };

  // 分类过滤对话框处理函数
  const handleOpenCategoryFilter = () => {
    setSelectedCategories([...appliedFilters.categories]);
    setCategoryFilterVisible(true);
  };

  const handleCategoryFilterCancel = () => {
    setCategoryFilterVisible(false);
    setSelectedCategories([...appliedFilters.categories]);
  };

  const handleCategoryFilterOk = () => {
    // 应用过滤器
    fetchProducts(1, pagination.pageSize, selectedCategories);
    setCategoryFilterVisible(false);
  };

  // 批量操作辅助函数
  const handleSelectProduct = (productId, checked) => {
    if (checked) {
      setSelectedProductIds([...selectedProductIds, productId]);
    } else {
      setSelectedProductIds(selectedProductIds.filter(id => id !== productId));
      setSelectAllPages(false); // 取消单个选择时，清除跨页全选
    }
  };

  const handleSelectAllInPage = (checked) => {
    if (checked) {
      const currentPageIds = products.map(p => p.id);
      setSelectedProductIds(currentPageIds);
    } else {
      setSelectedProductIds([]);
      setSelectAllPages(false);
    }
  };

  const isAllInPageSelected = () => {
    if (products.length === 0) return false;
    return products.every(p => selectedProductIds.includes(p.id));
  };

  const isSomeInPageSelected = () => {
    if (products.length === 0) return false;
    return products.some(p => selectedProductIds.includes(p.id)) && !isAllInPageSelected();
  };

  // 批量删除
  const handleBatchDelete = async () => {
    try {
      setBatchLoading(true);
      const payload = selectAllPages ? {
        use_filter: true,
        filter_categories: appliedFilters.categories.length > 0 ? appliedFilters.categories : null
      } : {
        use_filter: false,
        product_ids: selectedProductIds
      };
      
      const response = await productAPI.productsBatchDelete(payload);
      message.success(response.data.message || `成功删除 ${response.data.count} 个商品`);
      
      setSelectedProductIds([]);
      setSelectAllPages(false);
      setBatchDeleteVisible(false);
      fetchProducts();
    } catch (error) {
      message.error('批量删除失败');
      
    } finally {
      setBatchLoading(false);
    }
  };

  // 批量更新状态
  const handleBatchUpdateStatus = async () => {
    try {
      setBatchLoading(true);
      const payload = selectAllPages ? {
        use_filter: true,
        filter_categories: appliedFilters.categories.length > 0 ? appliedFilters.categories : null,
        is_active: batchStatusValue
      } : {
        use_filter: false,
        product_ids: selectedProductIds,
        is_active: batchStatusValue
      };
      
      const response = await productAPI.productsBatchUpdateStatus(payload);
      message.success(response.data.message || `成功更新 ${response.data.count} 个商品`);
      
      setSelectedProductIds([]);
      setSelectAllPages(false);
      setBatchStatusVisible(false);
      fetchProducts();
    } catch (error) {
      message.error('批量更新状态失败');
      
    } finally {
      setBatchLoading(false);
    }
  };

  // 批量更新分类
  const handleBatchUpdateCategory = async () => {
    if (!batchCategoryValue) {
      message.warning('请选择分类');
      return;
    }
    
    try {
      setBatchLoading(true);
      const payload = selectAllPages ? {
        use_filter: true,
        filter_categories: appliedFilters.categories.length > 0 ? appliedFilters.categories : null,
        category_id: batchCategoryValue
      } : {
        use_filter: false,
        product_ids: selectedProductIds,
        category_id: batchCategoryValue
      };
      
      const response = await productAPI.productsBatchUpdateCategory(payload);
      message.success(response.data.message || `成功更新 ${response.data.count} 个商品`);
      
      setSelectedProductIds([]);
      setSelectAllPages(false);
      setBatchCategoryVisible(false);
      setBatchCategoryValue(null);
      fetchProducts();
    } catch (error) {
      message.error('批量更新分类失败');
      
    } finally {
      setBatchLoading(false);
    }
  };

  // 批量更新物流渠道
  const handleBatchUpdateFreight = async () => {
    try {
      setBatchLoading(true);
      const payload = selectAllPages ? {
        use_filter: true,
        filter_categories: appliedFilters.categories.length > 0 ? appliedFilters.categories : null,
        freight_channel_ids: batchFreightValue
      } : {
        use_filter: false,
        product_ids: selectedProductIds,
        freight_channel_ids: batchFreightValue
      };
      
      const response = await productAPI.productsBatchUpdateFreight(payload);
      message.success(response.data.message || `成功更新 ${response.data.count} 个商品`);
      
      setSelectedProductIds([]);
      setSelectAllPages(false);
      setBatchFreightVisible(false);
      setBatchFreightValue([]);
      fetchProducts();
    } catch (error) {
      message.error('批量更新物流渠道失败');
      
    } finally {
      setBatchLoading(false);
    }
  };

  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      if (taskPolling) {
        clearInterval(taskPolling);
      }
    };
  }, [taskPolling]);

  // 页面加载时检查任务状态
  useEffect(() => {
    let mounted = true; // 防止组件卸载后setState
    
    const checkTaskStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/csv-import/status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        if (response.ok && mounted) {
          const status = await response.json();
          
          
          // 只有当获取到有效状态时才更新
          if (status && typeof status === 'object') {
            setTaskStatus(status);
            
            if (status.has_task && (status.status === 'processing' || status.status === 'pending')) {
              // 有正在进行的任务，开始轮询
              
              startTaskPolling();
            }
          }
        }
      } catch (error) {
        
      }
    };
    
    checkTaskStatus();
    
    return () => {
      mounted = false;
    };
  }, []); // 移除依赖项，只在组件挂载时执行一次

  const columns = [
    {
      title: (
        <Checkbox
          checked={isAllInPageSelected()}
          indeterminate={isSomeInPageSelected()}
          onChange={(e) => handleSelectAllInPage(e.target.checked)}
        />
      ),
      key: 'selection',
      width: 50,
      fixed: 'left',
      render: (_, record) => (
        <Checkbox
          checked={selectedProductIds.includes(record.id)}
          onChange={(e) => handleSelectProduct(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: '封面图片',
      dataIndex: 'cover_picture',
      key: 'cover_picture',
      width: 80,
      render: (coverPictureId, record) => {
        const coverImage = record.images?.find(img => img.id === coverPictureId);
        const hasMultipleImages = record.images && record.images.length > 1;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {coverImage ? (
              <div style={{ position: 'relative' }}>
                <Image
                  src={`${API_BASE_URL}/products/${record.id}/images/${coverImage.id}`}
                  width={40}
                  height={40}
                  style={{ objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                  preview={false}
                  onClick={() => navigate(`/product/${record.id}`)}
                />
                {hasMultipleImages && (
                  <div style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    fontSize: '10px',
                    padding: '1px 4px',
                    borderRadius: '2px'
                  }}>
                    {record.images.length}
                  </div>
                )}
              </div>
            ) : record.images && record.images.length > 0 ? (
              <div style={{ position: 'relative' }}>
                <Image
                  src={`${API_BASE_URL}/products/${record.id}/images/${record.images[0].id}`}
                  width={40}
                  height={40}
                  style={{ objectFit: 'cover', borderRadius: '4px', cursor: 'pointer', opacity: 0.7 }}
                  preview={false}
                  onClick={() => navigate(`/product/${record.id}`)}
                />
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  left: '2px',
                  backgroundColor: '#ff4d4f',
                  color: 'white',
                  fontSize: '8px',
                  padding: '1px 3px',
                  borderRadius: '2px'
                }}>
                  无封面
                </div>
                {hasMultipleImages && (
                  <div style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    fontSize: '10px',
                    padding: '1px 4px',
                    borderRadius: '2px'
                  }}>
                    {record.images.length}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ 
                width: '40px', 
                height: '40px', 
                backgroundColor: '#f0f0f0', 
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: '#999'
              }}>
                无图片
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '商品名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: '25%',
      minWidth: 150,
      render: (name, record) => (
        <Button 
          type="link" 
          style={{ padding: 0, height: 'auto', fontWeight: 'normal' }}
          onClick={() => window.open(`/product/${record.id}`, '_blank')}
        >
          {name}
        </Button>
      ),
    },
    {
      title: '价格',
      dataIndex: 'price',
      key: 'price',
      width: '10%',
      sorter: true,
      sortOrder: sorterState.field === 'price' ? sorterState.order : null,
      render: (_, record) => {
        const display = getPriceRangeDisplay(record.variants);
        return display === '-' ? <Text type="secondary">-</Text> : <Text>{display}</Text>;
      },
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>分类</span>
          <Button 
            type="link" 
            size="small" 
            style={{ padding: 0, height: 'auto' }}
            onClick={handleOpenCategoryFilter}
          >
            {appliedFilters.categories.length > 0 ? 
              `过滤(${appliedFilters.categories.length}项)` : 
              '过滤'
            }
          </Button>
        </div>
      ),
      dataIndex: 'category',
      key: 'category',
      width: '12%',
      ellipsis: true,
      render: (category) => category?.name || '未分类',
    },
    {
      title: '详细信息',
      key: 'variants',
      render: (_, record) => {
        if (!record.variants) {
          return <Text type="secondary">-</Text>;
        }
        
        // 判断是否为单品
        const isSimple = !record.variant_types || record.variant_types.length === 0;
        
        if (isSimple) {
          // 单品
          if (Array.isArray(record.variants) && record.variants.length > 0) {
            const variant = record.variants[0];
            return (
              <div>
                <div>价格: {variant.currency} {variant.price}</div>
                <div>库存: {variant.stock === -1 ? '无限' : variant.stock}</div>
                <div>重量: {variant.weight || 100}g</div>
                <div>SKU: {variant.SKU}</div>
              </div>
            );
          } else {
            return <Text type="secondary">无规格信息</Text>;
          }
        } else {
          // 多规格
          const variantCount = countVariants(record.variants);
          return (
            <div>
              <Tag color="blue">{variantCount} 个规格</Tag>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {(record.variant_types || []).join(', ')}
              </div>
            </div>
          );
        }
      },
      width: '20%',
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Switch checked={isActive} disabled />
      ),
      width: '8%',
    },
    {
      title: '购买数',
      dataIndex: 'purchase_count',
      key: 'purchase_count',
      render: (count) => (
        <Text>{count || 0}</Text>
      ),
      width: '8%',
      sorter: true,
      sortOrder: sorterState.field === 'purchase_count' ? sorterState.order : null,
    },
    {
      title: '连接数',
      dataIndex: 'connect_count',
      key: 'connect_count',
      render: (count) => (
        <Text>{count || 0}</Text>
      ),
      width: '8%',
      sorter: true,
      sortOrder: sorterState.field === 'connect_count' ? sorterState.order : null,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('zh-CN'),
      width: '15%',
      sorter: true,
      sortOrder: sorterState.field === 'created_at' ? sorterState.order : null,
    },
    {
      title: '操作',
      key: 'action',
      width: '20%',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="default"
            size="small"
            onClick={() => handleOpenNotesModal(record)}
          >
            备注
          </Button>
          {record.source_info && (
            <Button
              type="default"
              size="small"
              onClick={() => handleShowSourceInfo(record.source_info)}
            >
              来源信息
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个商品吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>商品管理</h1>
        <Space>
          {showCsvButton === 'processing' && (
            <Button type="link" onClick={handleShowTaskStatus}>
              正在导入CSV...
            </Button>
          )}
          {showCsvButton === 'import' && (
            <Button
              icon={<UploadOutlined />}
              onClick={handleOpenCsvImport}
            >
              从马帮CSV导入商品
            </Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            添加商品
          </Button>
        </Space>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Input.Search
          placeholder="搜索商品名称或描述"
          allowClear
          enterButton="搜索"
          value={searchKeyword}
          onChange={handleSearchChange}
          onSearch={handleSearch}
          style={{ maxWidth: 320 }}
        />
      </div>

      {/* 批量操作栏 */}
      {selectedProductIds.length > 0 && (
        <div style={{ 
          marginBottom: 16, 
          padding: '12px 16px', 
          backgroundColor: '#e6f7ff', 
          borderRadius: '4px',
          border: '1px solid #91d5ff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Text strong>
                {selectAllPages 
                  ? `已选择 ${pagination.total} 个商品（所有符合条件的商品）` 
                  : `已选择 ${selectedProductIds.length} 个商品`}
              </Text>
              {isAllInPageSelected() && !selectAllPages && pagination.total > pagination.pageSize && (
                <Checkbox
                  checked={selectAllPages}
                  onChange={(e) => setSelectAllPages(e.target.checked)}
                >
                  选择所有 {pagination.total} 个商品（共 {Math.ceil(pagination.total / pagination.pageSize)} 页）
                </Checkbox>
              )}
            </div>
            <Space>
              <Button
                onClick={() => setBatchStatusVisible(true)}
              >
                设置状态
              </Button>
              <Button
                onClick={() => setBatchCategoryVisible(true)}
              >
                设置分类
              </Button>
              <Button
                onClick={() => setBatchFreightVisible(true)}
              >
                设置物流渠道
              </Button>
              <Button
                onClick={handleOpenBulkNotesModal}
              >
                追加备注
              </Button>
              <Button
                onClick={() => setBulkNotesDeleteVisible(true)}
              >
                删除备注
              </Button>
              <Button
                danger
                onClick={() => setBatchDeleteVisible(true)}
              >
                批量删除
              </Button>
              <Button
                type="link"
                onClick={() => {
                  setSelectedProductIds([]);
                  setSelectAllPages(false);
                }}
              >
                取消选择
              </Button>
            </Space>
          </div>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={products}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        size="middle"
      />



      {/* 图片画廊Modal */}
      <Modal
        open={galleryVisible}
        title={`${currentProduct?.name} - 图片 (${currentImageIndex + 1}/${currentProduct?.images?.length || 0})`}
        footer={null}
        onCancel={() => setGalleryVisible(false)}
        width={800}
        centered
      >
        {currentProduct && currentProduct.images && currentProduct.images.length > 0 && (
          <div style={{ textAlign: 'center' }}>
            {/* 主图片显示 */}
            <div style={{ position: 'relative', marginBottom: 16 }}>
              <img
                alt={`商品图片 ${currentImageIndex + 1}`}
                style={{ 
                  width: '100%', 
                  maxHeight: '500px', 
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
                src={`${API_BASE_URL}/products/${currentProduct.id}/images/${currentProduct.images[currentImageIndex].id}`}
              />
              
              {/* 封面标识 */}
              {currentProduct.cover_picture === currentProduct.images[currentImageIndex].id && (
                <div style={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  backgroundColor: '#1890ff',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <StarFilled /> 封面图片
                </div>
              )}

              {/* 导航按钮 */}
              {currentProduct.images.length > 1 && (
                <>
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<LeftOutlined />}
                    style={{
                      position: 'absolute',
                      left: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 1
                    }}
                    onClick={() => handleImageChange('prev')}
                  />
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<RightOutlined />}
                    style={{
                      position: 'absolute',
                      right: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 1
                    }}
                    onClick={() => handleImageChange('next')}
                  />
                </>
              )}
            </div>

            {/* 缩略图导航 */}
            {currentProduct.images.length > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '8px', 
                flexWrap: 'wrap',
                maxHeight: '120px',
                overflowY: 'auto'
              }}>
                {currentProduct.images.map((img, index) => (
                  <div
                    key={img.id}
                    style={{
                      position: 'relative',
                      cursor: 'pointer',
                      border: index === currentImageIndex ? '2px solid #1890ff' : '2px solid transparent',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img
                      src={`${API_BASE_URL}/products/${currentProduct.id}/images/${img.id}`}
                      alt={`Thumbnail ${index + 1}`}
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        opacity: index === currentImageIndex ? 1 : 0.7
                      }}
                    />
                    {currentProduct.cover_picture === img.id && (
                      <StarFilled 
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          color: '#1890ff',
                          fontSize: '12px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          padding: '1px'
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* 操作按钮 */}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: '8px' }}>
              <Button
                type="primary"
                icon={<StarOutlined />}
                onClick={() => handleSetCover(currentProduct.id, currentProduct.images[currentImageIndex].id)}
                disabled={currentProduct.cover_picture === currentProduct.images[currentImageIndex].id}
              >
                {currentProduct.cover_picture === currentProduct.images[currentImageIndex].id ? '当前封面' : '设为封面'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* CSV导入对话框 */}
      <Modal
        title="从马帮CSV导入商品"
        open={csvModalVisible}
        onCancel={() => {
          setCsvModalVisible(false);
          csvForm.resetFields();
          setCsvFile(null);
          setCsvImportLoading(false); // 重置loading状态
        }}
        footer={null}
        width={600}
      >
        <Form
          form={csvForm}
          onFinish={handleCsvUpload}
          layout="vertical"
        >
          <Form.Item label="CSV文件">
            <Upload
              beforeUpload={() => false} // 阻止自动上传
              onChange={handleCsvFileChange}
              accept=".csv"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>选择CSV文件</Button>
            </Upload>
            {csvFile && (
              <div style={{ marginTop: 8, color: '#666' }}>
                已选择文件: {csvFile.name}
              </div>
            )}
          </Form.Item>

          <Form.Item
            name="category_id"
            label="分类"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select 
              placeholder="选择商品分类"
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {categories.map(category => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="supported_freight_channels"
            label="支持的物流渠道"
          >
            <Select
              mode="multiple"
              placeholder="选择支持的物流渠道"
              allowClear
            >
              {freightChannels.map(channel => (
                <Option key={channel.id} value={channel.id}>
                  {channel.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="collection_ids"
            label="添加到商品集"
          >
            <Select
              mode="multiple"
              placeholder="选择要添加的商品集"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {collections.map(collection => (
                <Option key={collection.id} value={collection.id}>
                  {collection.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="is_active"
            label="启用"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          {/* 新增功能选项 */}
          <div style={{ marginTop: 16, padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <h4 style={{ marginBottom: 16, color: '#1890ff' }}>导入选项</h4>
            
            {/* 币种选择 */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <span>币种选择:</span>
              </div>
              <Select
                value={selectedCurrency}
                onChange={setSelectedCurrency}
                style={{ width: 120 }}
              >
                <Option value="USD">USD</Option>
                <Option value="CNY">CNY</Option>
              </Select>
            </div>
            
            {/* 价格除以汇率 */}
            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={convertPriceByExchangeRate}
                onChange={(e) => setConvertPriceByExchangeRate(e.target.checked)}
              >
                价格除以汇率
              </Checkbox>
              {convertPriceByExchangeRate && (
                <div style={{ marginLeft: 24, marginTop: 8 }}>
                  <Space>
                    <span>汇率:</span>
                    <InputNumber
                      value={exchangeRate}
                      onChange={setExchangeRate}
                      min={0.1}
                      max={20}
                      step={0.1}
                      precision={2}
                      style={{ width: 100 }}
                    />
                  </Space>
                </div>
              )}
            </div>

            {/* 按百分比加价 */}
            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={percentageMarkup}
                onChange={(e) => setPercentageMarkup(e.target.checked)}
              >
                按百分比加价
              </Checkbox>
              {percentageMarkup && (
                <div style={{ marginLeft: 24, marginTop: 8 }}>
                  <Space>
                    <span>加价:</span>
                    <InputNumber
                      value={markupPercent}
                      onChange={setMarkupPercent}
                      min={1}
                      max={100}
                      precision={0}
                      style={{ width: 80 }}
                    />
                    <span>%</span>
                  </Space>
                </div>
              )}
            </div>

            {/* 价格往上靠 */}
            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={priceRounding}
                onChange={(e) => setPriceRounding(e.target.checked)}
              >
                价格往上靠
              </Checkbox>
              {priceRounding && (
                <div style={{ marginLeft: 24, marginTop: 8 }}>
                  <Radio.Group
                    value={roundingType}
                    onChange={(e) => setRoundingType(e.target.value)}
                  >
                    <Radio value="ceil">向上取整</Radio>
                    <Radio value="99">取x.99</Radio>
                  </Radio.Group>
                </div>
              )}
            </div>

            {/* 最小重量 */}
            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={minWeight}
                onChange={(e) => setMinWeight(e.target.checked)}
              >
                最小重量
              </Checkbox>
              {minWeight && (
                <div style={{ marginLeft: 24, marginTop: 8 }}>
                  <Space>
                    <InputNumber
                      value={minWeightValue}
                      onChange={setMinWeightValue}
                      min={1}
                      precision={0}
                      style={{ width: 100 }}
                    />
                    <span>克</span>
                  </Space>
                </div>
              )}
            </div>

            {/* 加重 */}
            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={addWeight}
                onChange={(e) => {
                  setAddWeight(e.target.checked);
                  // 根据选择的类型设置默认值
                  if (e.target.checked) {
                    setWeightValue(weightType === 'fixed' ? 200 : 15);
                  }
                }}
              >
                每件商品加重（包装之类的）
              </Checkbox>
              {addWeight && (
                <div style={{ marginLeft: 24, marginTop: 8 }}>
                  <Radio.Group
                    value={weightType}
                    onChange={(e) => {
                      setWeightType(e.target.value);
                      // 切换类型时更新默认值
                      setWeightValue(e.target.value === 'fixed' ? 200 : 15);
                    }}
                  >
                    <Radio value="fixed">加固定重量</Radio>
                    <Radio value="percentage">按百分比添加</Radio>
                  </Radio.Group>
                  <div style={{ marginTop: 8 }}>
                    <Space>
                      <InputNumber
                        value={weightValue}
                        onChange={setWeightValue}
                        min={weightType === 'fixed' ? 1 : 1}
                        max={weightType === 'fixed' ? 10000 : 100}
                        precision={0}
                        style={{ width: 100 }}
                      />
                      <span>{weightType === 'fixed' ? '克' : '%'}</span>
                    </Space>
                  </div>
                </div>
              )}
            </div>

            {/* AI洗商品描述 */}
            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={aiCleanDescription}
                onChange={(e) => setAiCleanDescription(e.target.checked)}
              >
                用AI洗商品描述（同时会翻译成英文）
              </Checkbox>
            </div>

            {/* AI翻译商品标题 */}
            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={aiTranslateTitle}
                onChange={(e) => setAiTranslateTitle(e.target.checked)}
              >
                用AI把商品名称翻译成英文
              </Checkbox>
            </div>

            {/* AI翻译商品规格 */}
            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={aiTranslateVariant}
                onChange={(e) => setAiTranslateVariant(e.target.checked)}
              >
                用AI翻译商品规格信息
              </Checkbox>
            </div>

            {/* 不下载图片 */}
            <div style={{ marginBottom: 0 }}>
              <Checkbox
                checked={noDownloadImages}
                onChange={(e) => setNoDownloadImages(e.target.checked)}
              >
                不下载图片 (Debug)
              </Checkbox>
            </div>
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button 
                onClick={() => {
                  setCsvModalVisible(false);
                  setCsvImportLoading(false); // 重置loading状态
                }}
              >
                取消
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={csvImportLoading}
                disabled={csvImportLoading}
              >
                开始导入
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 任务状态对话框 */}
      <Modal
        title="CSV导入进度"
        open={taskStatusVisible}
        onCancel={() => setTaskStatusVisible(false)}
        footer={[
          <Button key="close" onClick={() => setTaskStatusVisible(false)}>
            关闭
          </Button>
        ]}
        width={500}
      >
        {(stableTaskStatus || taskStatus) && (
          <div>
            {(() => {
              // 优先使用stableTaskStatus，如果没有则使用taskStatus
              const currentStatus = stableTaskStatus || taskStatus;
              return (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <strong>任务状态：</strong>
                    <Tag color={
                      currentStatus.status === 'processing' ? 'blue' :
                      currentStatus.status === 'completed' ? 'green' :
                      currentStatus.status === 'error' ? 'red' : 'orange'
                    }>
                      {
                        currentStatus.status === 'processing' ? '处理中' :
                        currentStatus.status === 'completed' ? '完成' :
                        currentStatus.status === 'error' ? '错误' : 
                        currentStatus.status === 'pending' ? '准备中' : '等待中'
                      }
                    </Tag>
                  </div>
                  
                  {currentStatus.has_task && (
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>总行数：</strong>{currentStatus.total_rows || 0}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>已处理行数：</strong>{currentStatus.processed_rows || 0}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>发现商品：</strong>{currentStatus.products_found || 0}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>发现SKU：</strong>{currentStatus.variants_found || 0}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>下载图片：</strong>{currentStatus.images_downloaded || 0}
                      </div>
                      
                      {currentStatus.status === 'error' && currentStatus.error_message && (
                        <div style={{ marginTop: 16 }}>
                          <strong style={{ color: 'red' }}>错误信息：</strong>
                          <div style={{ color: 'red', marginTop: 4 }}>
                            {currentStatus.error_message}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </Modal>

      {/* 商品备注对话框 */}
      <Modal
        title={notesTargetProduct ? `${notesTargetProduct.name} - 备注` : '备注'}
        open={notesModalVisible}
        onCancel={handleCloseNotesModal}
        footer={[
          <Button key="cancel" onClick={handleCloseNotesModal} disabled={notesSaving}>
            取消
          </Button>,
          <Button key="submit" type="primary" loading={notesSaving} onClick={handleSubmitNotes}>
            提交
          </Button>,
        ]}
        destroyOnClose
        width={700}
      >
        <Input.TextArea
          rows={10}
          value={notesValue}
          onChange={(e) => setNotesValue(e.target.value)}
          placeholder="请输入商品备注"
          disabled={notesLoading}
          style={{ minHeight: 200 }}
        />
      </Modal>

      {/* 来源信息对话框 */}
      <Modal
        title="商品来源信息"
        open={sourceInfoVisible}
        onCancel={() => setSourceInfoVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSourceInfoVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {currentSourceInfo && (
          <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
            {Object.entries(currentSourceInfo).map(([key, value]) => (
              <div key={key} style={{ marginBottom: '8px' }}>
                <Text strong style={{ color: '#1890ff' }}>{key}:</Text>
                <div style={{ marginLeft: '8px', marginTop: '2px' }}>
                  <Text>{value}</Text>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* 分类过滤对话框 */}
      <Modal
        title="按分类过滤商品"
        open={categoryFilterVisible}
        onCancel={handleCategoryFilterCancel}
        onOk={handleCategoryFilterOk}
        okText="应用过滤"
        cancelText="取消"
        width={600}
        bodyStyle={{ maxHeight: '400px', overflowY: 'auto' }}
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button size="small" onClick={handleClearSelection}>清除选择</Button>
          </Space>
          {selectedCategories.length > 0 && (
            <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
              已选择 {selectedCategories.length} 个分类
            </div>
          )}
        </div>

        {categories.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', padding: '50px 0' }}>
            暂无分类数据
          </div>
        ) : (
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="选择要过滤的分类"
            value={selectedCategories}
            onChange={setSelectedCategories}
            style={{ width: '100%' }}
            optionFilterProp="children"
            maxTagCount="responsive"
          >
            {categories.map(category => (
              <Option key={category.id} value={category.id}>
                {category.name}
              </Option>
            ))}
          </Select>
        )}
      </Modal>

      {/* 批量删除对话框 */}
      <Modal
        title="批量删除商品"
        open={batchDeleteVisible}
        onCancel={() => setBatchDeleteVisible(false)}
        onOk={handleBatchDelete}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true, loading: batchLoading }}
        cancelButtonProps={{ disabled: batchLoading }}
      >
        <p>确定要删除选中的商品吗？此操作不可恢复。</p>
        <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
          {selectAllPages 
            ? `将删除所有符合当前过滤条件的 ${pagination.total} 个商品` 
            : `将删除 ${selectedProductIds.length} 个商品`}
        </p>
      </Modal>

      {/* 批量设置状态对话框 */}
      <Modal
        title="批量设置状态"
        open={batchStatusVisible}
        onCancel={() => setBatchStatusVisible(false)}
        onOk={handleBatchUpdateStatus}
        okText="确认"
        cancelText="取消"
        okButtonProps={{ loading: batchLoading }}
        cancelButtonProps={{ disabled: batchLoading }}
      >
        <Form layout="vertical">
          <Form.Item label="商品状态">
            <Radio.Group
              value={batchStatusValue}
              onChange={(e) => setBatchStatusValue(e.target.value)}
            >
              <Radio value={true}>启用</Radio>
              <Radio value={false}>禁用</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
        <p style={{ color: '#666', marginTop: 16 }}>
          {selectAllPages 
            ? `将更新所有符合当前过滤条件的 ${pagination.total} 个商品` 
            : `将更新 ${selectedProductIds.length} 个商品`}
        </p>
      </Modal>

      {/* 批量设置分类对话框 */}
      <Modal
        title="批量设置分类"
        open={batchCategoryVisible}
        onCancel={() => {
          setBatchCategoryVisible(false);
          setBatchCategoryValue(null);
        }}
        onOk={handleBatchUpdateCategory}
        okText="确认"
        cancelText="取消"
        okButtonProps={{ loading: batchLoading }}
        cancelButtonProps={{ disabled: batchLoading }}
      >
        <Form layout="vertical">
          <Form.Item label="商品分类" required>
            <Select
              placeholder="请选择商品分类"
              value={batchCategoryValue}
              onChange={setBatchCategoryValue}
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {categories.map(category => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
        <p style={{ color: '#666', marginTop: 16 }}>
          {selectAllPages 
            ? `将更新所有符合当前过滤条件的 ${pagination.total} 个商品` 
            : `将更新 ${selectedProductIds.length} 个商品`}
        </p>
      </Modal>

      {/* 批量设置物流渠道对话框 */}
      <Modal
        title="批量设置物流渠道"
        open={batchFreightVisible}
        onCancel={() => {
          setBatchFreightVisible(false);
          setBatchFreightValue([]);
        }}
        onOk={handleBatchUpdateFreight}
        okText="确认"
        cancelText="取消"
        okButtonProps={{ loading: batchLoading }}
        cancelButtonProps={{ disabled: batchLoading }}
      >
        <Form layout="vertical">
          <Form.Item label="支持的物流渠道">
            <Select
              mode="multiple"
              placeholder="选择支持的物流渠道"
              value={batchFreightValue}
              onChange={setBatchFreightValue}
              allowClear
            >
              {freightChannels.map(channel => (
                <Option key={channel.id} value={channel.id}>
                  {channel.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
        <p style={{ color: '#666', marginTop: 16 }}>
          {selectAllPages 
            ? `将更新所有符合当前过滤条件的 ${pagination.total} 个商品` 
            : `将更新 ${selectedProductIds.length} 个商品`}
        </p>
      </Modal>

      {/* 批量追加备注对话框 */}
      <Modal
        title="批量追加备注"
        open={bulkNotesModalVisible}
        onCancel={handleCloseBulkNotesModal}
        footer={[
          <Button key="cancel" onClick={handleCloseBulkNotesModal} disabled={bulkNotesLoading}>
            取消
          </Button>,
          <Button key="submit" type="primary" loading={bulkNotesLoading} onClick={handleBatchAppendNotes}>
            提交
          </Button>,
        ]}
        destroyOnClose
        width={700}
      >
        <Input.TextArea
          rows={8}
          value={bulkNotesContent}
          onChange={(e) => setBulkNotesContent(e.target.value)}
          placeholder="请输入要追加的备注内容"
          disabled={bulkNotesLoading}
          style={{ minHeight: 180 }}
        />
        <div style={{ marginTop: 16 }}>
          <Text strong>追加位置</Text>
          <div style={{ marginTop: 8 }}>
            <Radio.Group
              value={bulkNotesPosition}
              onChange={(e) => setBulkNotesPosition(e.target.value)}
              disabled={bulkNotesLoading}
            >
              <Radio value="before">追加在已有备注之前</Radio>
              <Radio value="after">追加在已有备注之后</Radio>
            </Radio.Group>
          </div>
        </div>
      </Modal>

      {/* 批量删除备注对话框 */}
      <Modal
        title="批量删除备注"
        open={bulkNotesDeleteVisible}
        onCancel={() => {
          if (!bulkNotesDeleteLoading) {
            setBulkNotesDeleteVisible(false);
          }
        }}
        footer={[
          <Button key="cancel" onClick={() => setBulkNotesDeleteVisible(false)} disabled={bulkNotesDeleteLoading}>
            取消
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={bulkNotesDeleteLoading}
            onClick={handleBatchDeleteNotes}
          >
            确认删除
          </Button>,
        ]}
        destroyOnClose
      >
        <p>确定要删除选中商品的备注吗？此操作会清空备注内容。</p>
        <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
          {selectAllPages 
            ? `将清空所有符合当前过滤条件的 ${pagination.total} 个商品备注`
            : `将清空 ${selectedProductIds.length} 个商品备注`}
        </p>
      </Modal>
    </div>
  );
};

export default ProductManagement;
